import {
  Button,
  Combobox,
  LayoutGridIcon,
  Text,
  ToggleGroup,
  IdeasIcon,
  ScrollTextIcon,
  BookOpenTextIcon,
  PickaxeIcon,
  QuestionIcon,
  ClapperboardIcon,
  CommentSquareQuoteIcon,
  ClipboardListIcon,
  TentTreeIcon,
} from '@buffer-mono/popcorn'

import { FilterButton } from '~publish/components/FilterButton'
import styles from './Filters.module.css'
import {
  GOALS,
  STRUCTURES,
  TYPES,
  USER_TYPES,
} from '../../notion-sync/interface'
import { useTemplateTracking } from '../../hooks/useTemplateTracking'

const templateTypeIconMap = {
  Tip: <IdeasIcon />,
  'Case Study': <ScrollTextIcon />,
  Story: <BookOpenTextIcon />,
  'How-to': <PickaxeIcon />,
  Question: <QuestionIcon />,
  'Behind the scenes': <ClapperboardIcon />,
  Opinion: <CommentSquareQuoteIcon />,
  List: <ClipboardListIcon />,
}

export const FilterByTemplateType = ({
  value = 'All',
  onChange,
  // TODO: creator-camp-cleanup
  isCreatorCampActive,
}: {
  value: string | undefined
  onChange: (value: string) => void
  // TODO: creator-camp-cleanup
  isCreatorCampActive: boolean
}): JSX.Element => {
  const { trackTemplateFiltered } = useTemplateTracking()

  const handleToggleGroupChange = (newValue: string): void => {
    // Ensure a value is always set
    if (newValue && newValue !== value) {
      trackTemplateFiltered({
        filterType: 'category',
        filterValue: newValue,
      })
      onChange(newValue)
    }
  }

  return (
    <ToggleGroup
      size="medium"
      className={styles.typeToggleGroup}
      value={value}
      onChange={handleToggleGroupChange}
    >
      <ToggleGroup.Item value="All" className={styles.typeToggleGroupItem}>
        <LayoutGridIcon />
        All
      </ToggleGroup.Item>
      {TYPES.map((type) => (
        <ToggleGroup.Item
          key={type}
          value={type}
          className={styles.typeToggleGroupItem}
        >
          {templateTypeIconMap[type]}
          {type}
        </ToggleGroup.Item>
      ))}
      {/* TODO: creator-camp-cleanup */}
      {isCreatorCampActive && (
        <ToggleGroup.Item
          value="Creator Camp"
          className={styles.typeToggleGroupItem}
        >
          <TentTreeIcon />
          Creator Camp
        </ToggleGroup.Item>
      )}
    </ToggleGroup>
  )
}

export const MoreFilters = ({
  value = [],
  onChange,
  onReset,
}: {
  value: string[]
  onChange: (value: string[]) => void
  onReset: () => void
}): JSX.Element => {
  const { trackTemplateFiltered } = useTemplateTracking()

  const handleChange = (newValue: string[]): void => {
    // Track newly added filters
    const addedFilters = newValue.filter((item) => !value.includes(item))

    // Track each newly added filter
    addedFilters.forEach((filter) => {
      let filterType: 'madeFor' | 'format' | 'goal' = 'madeFor'

      if (USER_TYPES.includes(filter as any)) {
        filterType = 'madeFor'
      } else if (STRUCTURES.includes(filter as any)) {
        filterType = 'format'
      } else if (GOALS.includes(filter as any)) {
        filterType = 'goal'
      }

      trackTemplateFiltered({
        filterType,
        filterValue: filter,
      })
    })

    onChange(newValue)
  }

  const handleReset = (): void => {
    if (value.length > 0) {
      trackTemplateFiltered({
        filterType: 'category',
        filterValue: 'reset',
      })
    }
    onReset()
  }

  return (
    <Combobox multiple value={value} onChange={handleChange}>
      <Combobox.Trigger>
        <FilterButton
          size="medium"
          icon={<></>}
          count={value.length}
          variant="tertiary"
        >
          More
        </FilterButton>
      </Combobox.Trigger>
      <Combobox.Content className={styles.moreFiltersContent}>
        <Combobox.List>
          <Combobox.Group heading="Made for">
            {USER_TYPES.map((userType) => (
              <Combobox.Item key={userType} value={userType}>
                <Text weight="medium">{userType}</Text>
              </Combobox.Item>
            ))}
          </Combobox.Group>
          <Combobox.Separator />
          <Combobox.Group heading="Format">
            {STRUCTURES.map((structure) => (
              <Combobox.Item key={structure} value={structure}>
                <Text weight="medium">{structure}</Text>
              </Combobox.Item>
            ))}
          </Combobox.Group>
          <Combobox.Separator />
          <Combobox.Group heading="Goal">
            {GOALS.map((goal) => (
              <Combobox.Item key={goal} value={goal}>
                <Text weight="medium">{goal}</Text>
              </Combobox.Item>
            ))}
          </Combobox.Group>
        </Combobox.List>
        <Combobox.Footer>
          <Button
            variant="tertiary"
            className={styles.resetFiltersButton}
            onClick={handleReset}
          >
            Reset Filters
          </Button>
        </Combobox.Footer>
      </Combobox.Content>
    </Combobox>
  )
}
